import { matchPath } from 'react-router-dom'
import { tourContextRoute } from './anchors'
import type { ProductTour, ProductTourStep } from './types'

/** L'URL correspond à la route du contexte du tour (un contexte inconnu ne correspond à rien). */
export const tourMatchesPath = (
  tour: ProductTour,
  pathname: string
): boolean => {
  const route = tourContextRoute(tour.contextKey)
  return (
    route !== undefined &&
    matchPath({ path: route, end: true }, pathname) !== null
  )
}

export const toursForPath = (
  tours: readonly ProductTour[],
  pathname: string
): ProductTour[] =>
  tours.filter(
    (tour) => tour.steps.length > 0 && tourMatchesPath(tour, pathname)
  )

/**
 * Tour à lancer seul sur cette page : le premier avec `autoStart`, jamais vu, pas déjà proposé
 * pendant la session et disponible à l'écran. Au plus un par visite : l'appelant ne rappelle pas
 * après un lancement.
 */
export const pickAutoStartTour = (
  tours: readonly ProductTour[],
  pathname: string,
  proposedThisSession: ReadonlySet<string>,
  isAvailable: (tour: ProductTour) => boolean = () => true
): ProductTour | undefined =>
  toursForPath(tours, pathname).find(
    (tour) =>
      tour.autoStart &&
      tour.state === null &&
      !proposedThisSession.has(tour.id) &&
      isAvailable(tour)
  )

/**
 * Un tour est disponible quand le marqueur de son contexte est à l'écran : la route seule ne
 * distingue pas deux variantes d'un écran (classique et beta de /declaration).
 */
export const isTourAvailable = (
  tour: ProductTour,
  isContextDisplayed: (context: string) => boolean
): boolean => isContextDisplayed(tour.contextKey)

/**
 * Une étape est affichable sans cible (bulle centrée), quand son ancre est à l'écran, ou quand
 * elle a une action déclarée par l'écran : l'action ouvrira ce qui la contient.
 */
export const isStepDisplayable = (
  step: ProductTourStep,
  isAnchorDisplayable: (anchor: string) => boolean,
  isActionAvailable: (action: string) => boolean
): boolean =>
  step.anchor === null ||
  isAnchorDisplayable(step.anchor) ||
  (step.action !== null && isActionAvailable(step.action))

/**
 * Prochaine étape affichable dans la direction donnée, en sautant les autres.
 * `undefined` = plus rien dans cette direction.
 */
export const nextDisplayableStepIndex = (
  steps: readonly ProductTourStep[],
  from: number,
  direction: 1 | -1,
  isDisplayable: (step: ProductTourStep) => boolean
): number | undefined => {
  for (
    let index = from;
    index >= 0 && index < steps.length;
    index += direction
  ) {
    if (isDisplayable(steps[index])) return index
  }
  return undefined
}

/**
 * Étapes affichables, dans l'ordre : une étape dont l'ancre manque est retirée du parcours, et la
 * progression se compte sur ce qui reste (TEC-1631).
 */
export const displayableStepIndices = (
  steps: readonly ProductTourStep[],
  isDisplayable: (step: ProductTourStep) => boolean
): number[] =>
  steps.flatMap((step, index) => (isDisplayable(step) ? [index] : []))
